import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sessionsService, stationsService, usersService, ocppService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Search,
  AlertCircle,
  RefreshCw,
  Clock,
  StopCircle,
  Zap,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const START_METHODS = [
  { value: 'rfid', label: 'RFID Kart' },
  { value: 'qr', label: 'QR Kod' },
  { value: 'app', label: 'Uygulama' },
  { value: 'manual', label: 'Manuel' }
];

// Güç Göstergesi (Gauge) Komponenti
const PowerGauge = ({ power, maxPower = 22 }) => {
  const percentage = Math.min((power / maxPower) * 100, 100);
  const radius = 35;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (power === 0) return '#9ca3af';
    if (power < 7) return '#22c55e';
    if (power < 15) return '#eab308';
    return '#ef4444';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      <svg width="80" height="50" viewBox="0 0 80 50">
        {/* Arka plan yarım daire */}
        <path
          d="M 5 45 A 35 35 0 0 1 75 45"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Dolgu yarım daire */}
        <path
          d="M 5 45 A 35 35 0 0 1 75 45"
          fill="none"
          stroke={getColor()}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${(percentage / 100) * 110} 110`}
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
        {/* Değer */}
        <text x="40" y="40" textAnchor="middle" fontSize="14" fontWeight="bold" fill={getColor()}>
          {power.toFixed(1)}
        </text>
        <text x="40" y="48" textAnchor="middle" fontSize="8" fill="#6b7280">kW</text>
      </svg>
    </div>
  );
};

export default function SessionsPage() {
  const { user, isAdmin, getEffectiveRole, getEffectiveSiteId, isHesapYoneticisi, isCalisan } = useAuth();
  const effectiveRole = getEffectiveRole();
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveTechnician = effectiveRole === 'technician';
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveCalisan = effectiveRole === 'calisan' || effectiveRole === 'sofor';
  
  // Çalışan/Şoför için oturum başlatma butonlarını gizle
  const canCreateSession = !isEffectiveTechnician && !isEffectiveCalisan && !(isCalisan && isCalisan());
  
  const [sessions, setSessions] = useState([]);
  const [stations, setStations] = useState([]);
  const [users, setUsers] = useState([]);
  const [liveChargers, setLiveChargers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [stoppingSession, setStoppingSession] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [createFormData, setCreateFormData] = useState({
    station_id: '',
    user_id: '',
    start_method: 'app',
    rfid_card_id: ''
  });

  const [endFormData, setEndFormData] = useState({
    energy_consumed: '',
    cost: ''
  });

  // Her 10 saniyede currentTime güncelle (aktif oturum süreleri için)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const isActive = activeTab === 'active' ? true : activeTab === 'completed' ? false : undefined;
      const sessionsData = await sessionsService.list(isActive);
      
      let filteredSessions = sessionsData || [];
      
      // Hesap Yöneticisi için sadece kendi sitesinin oturumlarını göster
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        filteredSessions = filteredSessions.filter(s => s.site_id === effectiveSiteId);
      } else if (!isAdmin()) {
        filteredSessions = filteredSessions.filter(s => s.user_id === user?.id);
      }
      
      setSessions(filteredSessions);
      
      if (isAdmin() || isEffectiveHesapYoneticisi) {
        try {
          const [stationsData, usersData, chargersData] = await Promise.all([
            stationsService.list(),
            usersService.list(),
            ocppService.chargers()
          ]);
          
          // Hesap Yöneticisi için istasyonları filtrele
          let filteredStations = stationsData || [];
          if (isEffectiveHesapYoneticisi && effectiveSiteId) {
            filteredStations = filteredStations.filter(s => s.account_id === effectiveSiteId);
          }
          
          setStations(filteredStations);
          setUsers(usersData || []);
          setLiveChargers(chargersData || []);
        } catch (adminErr) {
          console.warn('Admin data fetch failed:', adminErr);
        }
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      if (errorMsg.includes('403') || errorMsg.includes('permission')) {
        setSessions([]);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Periyodik güncelleme (aktif oturumlar için)
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'active' || activeTab === 'all') {
        fetchData();
      }
    }, 10000); // 10 saniyede bir güncelle
    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // OCPP üzerinden şarjı durdur
  const handleRemoteStop = async (session) => {
    if (!session.transaction_id) {
      toast.error('Bu oturum için transaction ID bulunamadı');
      return;
    }
    
    // Şarj cihazı ID'sini bul
    const station = stations.find(s => s.id === session.station_id);
    const chargerId = station?.ocpp_id || session.station_id;
    
    setStoppingSession(session.id);
    try {
      await ocppService.remoteStop({
        charger_id: chargerId,
        transaction_id: session.transaction_id
      });
      toast.success('Şarj durdurma komutu gönderildi');
      setTimeout(fetchData, 2000);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStoppingSession(null);
    }
  };

  // Anlık güç değerini al
  const getLivePower = (session) => {
    const station = stations.find(s => s.id === session.station_id);
    const charger = liveChargers.find(c => c.charge_point_id === station?.ocpp_id);
    return charger?.current_power || 0;
  };

  // Tahmini tutar hesapla
  const getEstimatedCost = (session) => {
    const pricePerKwh = session.price_per_kwh || 5.0;
    const energy = session.energy_consumed || 0;
    return (energy * pricePerKwh).toFixed(2);
  };

  const resetCreateForm = () => {
    setCreateFormData({
      station_id: '',
      user_id: isAdmin() ? '' : user?.id,
      start_method: 'app',
      rfid_card_id: ''
    });
  };

  const openCreateModal = () => {
    resetCreateForm();
    setCreateModalOpen(true);
  };

  const openEndModal = (session) => {
    setSelectedSession(session);
    setEndFormData({
      energy_consumed: session.energy_consumed || '',
      cost: session.cost || ''
    });
    setEndModalOpen(true);
  };

  const openDetailModal = (session) => {
    setDetailSession(session);
    setDetailModalOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        station_id: createFormData.station_id,
        user_id: isAdmin() ? createFormData.user_id : user?.id,
        start_method: createFormData.start_method,
        rfid_card_id: createFormData.rfid_card_id || null
      };
      await sessionsService.create(payload);
      toast.success('Oturum başarıyla başlatıldı');
      setCreateModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEndSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        energy_consumed: parseFloat(endFormData.energy_consumed) || 0,
        cost: parseFloat(endFormData.cost) || 0
      };
      await sessionsService.end(selectedSession.id, payload);
      toast.success('Oturum basariyla sonlandirildi');
      setEndModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    const search = searchTerm.toLowerCase();
    return (
      session.station_name?.toLowerCase().includes(search) ||
      session.user_id?.toLowerCase().includes(search)
    );
  });

  const getStationName = (stationId) => {
    const station = stations.find(s => s.id === stationId);
    return station?.name || stationId || '-';
  };

  const getUserName = (userId) => {
    if (!isAdmin()) return '-';
    const u = users.find(u => u.id === userId);
    return u?.full_name || u?.email || userId || '-';
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    // UTC'yi UTC+3 (Türkiye saati) olarak göster
    try {
      const date = new Date(dateStr);
      // Eğer dateStr zaten 'Z' ile bitmiyorsa UTC olarak kabul et
      const utcTime = dateStr.endsWith('Z') || dateStr.includes('+') 
        ? date.getTime() 
        : date.getTime() - (date.getTimezoneOffset() * 60000);
      
      // UTC+3 için 3 saat ekle
      const trTime = new Date(utcTime + (3 * 60 * 60 * 1000));
      
      const day = String(trTime.getUTCDate()).padStart(2, '0');
      const month = String(trTime.getUTCMonth() + 1).padStart(2, '0');
      const year = trTime.getUTCFullYear();
      const hours = String(trTime.getUTCHours()).padStart(2, '0');
      const minutes = String(trTime.getUTCMinutes()).padStart(2, '0');
      const seconds = String(trTime.getUTCSeconds()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return dateStr;
    }
  };

  const formatDuration = (start, end, isActive = false) => {
    if (!start) return '-';
    
    // start_time UTC olarak geliyor ama timezone suffix olmayabilir
    // Eğer Z veya + yoksa, UTC olarak kabul et ve Z ekle
    let startStr = start;
    if (!startStr.endsWith('Z') && !startStr.includes('+')) {
      startStr = startStr + 'Z';
    }
    
    const startMs = new Date(startStr).getTime();
    
    // end_time için de aynısını yap
    let endMs;
    if (end) {
      let endStr = end;
      if (!endStr.endsWith('Z') && !endStr.includes('+')) {
        endStr = endStr + 'Z';
      }
      endMs = new Date(endStr).getTime();
    } else {
      endMs = Date.now();
    }
    
    // Milisaniye farkı
    let diffMs = endMs - startMs;
    
    // Negatif değer olmaması için kontrol
    if (diffMs < 0) diffMs = 0;
    
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}sa ${minutes}dk`;
    } else if (minutes > 0) {
      return `${minutes}dk ${seconds}sn`;
    } else {
      return `${seconds}sn`;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="sessions-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Şarj Oturumları</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>
            {isAdmin() ? 'Tüm şarj oturumlarını yönetin' : 'Şarj geçmişinizi görüntüleyin'}
          </p>
        </div>
        {/* Çalışan/Şoför için "Yeni Oturum" butonu gizle */}
        {canCreateSession && (
          <NativeButton onClick={openCreateModal} data-testid="create-session-button">
            <Plus style={{ width: '16px', height: '16px' }} />
            Yeni Oturum
          </NativeButton>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
        {[
          { value: 'all', label: 'Tümü' },
          { value: 'active', label: 'Aktif' },
          { value: 'completed', label: 'Tamamlanan' }
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: activeTab === tab.value ? '#2563eb' : 'transparent',
              color: activeTab === tab.value ? 'white' : '#64748b',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ position: 'relative' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} className="text-slate-400" />
            <input
              type="text"
              placeholder="Oturum ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 40px',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--card-bg)'
              }}
              className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              data-testid="search-sessions-input"
            />
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Sessions Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Clock}>
            Oturum Listesi ({filteredSessions.length})
          </NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          {filteredSessions.length === 0 ? (
            <EmptyState
              icon={Clock}
              title={isAdmin() ? 'Oturum bulunamadı' : 'Henüz aktif oturumunuz bulunmamaktadır'}
              description={isAdmin() 
                ? 'Arama kriterlerinize uygun oturum yok veya henüz oturum başlatılmamış.'
                : 'Şarj istasyonlarından birinde yeni bir oturum başlatabilirsiniz.'}
              action={canCreateSession && (
                <NativeButton variant="outline" onClick={openCreateModal}>
                  <Plus style={{ width: '16px', height: '16px' }} />
                  Yeni Oturum Başlat
                </NativeButton>
              )}
            />
          ) : (
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>İstasyon</NativeTableHead>
                {isAdmin() && <NativeTableHead>Kullanıcı</NativeTableHead>}
                <NativeTableHead>RFID Kart</NativeTableHead>
                <NativeTableHead>Baslangic</NativeTableHead>
                <NativeTableHead>Sure</NativeTableHead>
                <NativeTableHead>Guc</NativeTableHead>
                <NativeTableHead>Enerji</NativeTableHead>
                {isAdmin() && <NativeTableHead>Tutar</NativeTableHead>}
                <NativeTableHead>Durum</NativeTableHead>
                <NativeTableHead align="right">Islemler</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {filteredSessions.map((session) => {
                  const livePower = getLivePower(session);
                  const estimatedCost = getEstimatedCost(session);
                  
                  return (
                  <NativeTableRow 
                    key={session.id} 
                    data-testid={`session-row-${session.id}`}
                    onClick={() => openDetailModal(session)}
                    style={{ cursor: 'pointer' }}
                  >
                    <NativeTableCell>
                      <span style={{ fontWeight: '500' }}>{session.station_name || getStationName(session.station_id)}</span>
                    </NativeTableCell>
                    {isAdmin() && <NativeTableCell>{getUserName(session.user_id)}</NativeTableCell>}
                    <NativeTableCell>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6366f1' }}>
                        {session.rfid_tag || session.id_tag || '-'}
                      </span>
                    </NativeTableCell>
                    <NativeTableCell>{formatDateTime(session.start_time)}</NativeTableCell>
                    <NativeTableCell>{formatDuration(session.start_time, session.end_time, session.is_active || session.status === 'active')}</NativeTableCell>
                    <NativeTableCell>
                      {session.is_active ? (
                        <PowerGauge power={livePower} />
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </NativeTableCell>
                    <NativeTableCell>
                      {session.energy_consumed != null ? `${session.energy_consumed.toFixed(2)} kWh` : '-'}
                    </NativeTableCell>
                    {isAdmin() && (
                      <NativeTableCell>
                        {session.is_active ? (
                          <span style={{ color: '#f59e0b', fontWeight: '500' }}>~{estimatedCost} TL</span>
                        ) : (
                          session.cost != null ? `${session.cost.toFixed(2)} TL` : '-'
                        )}
                      </NativeTableCell>
                    )}
                    <NativeTableCell>
                      <NativeBadge variant={session.is_active ? 'default' : session.status === 'suspended' ? 'warning' : 'secondary'}>
                        {session.is_active ? 'Aktif' : session.status === 'suspended' ? 'Askıda' : 'Tamamlandi'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        {session.is_active && session.transaction_id && (
                          <NativeButton
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoteStop(session)}
                            disabled={stoppingSession === session.id}
                            data-testid={`stop-charging-${session.id}`}
                          >
                            {stoppingSession === session.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Zap style={{ width: '16px', height: '16px' }} />
                            )}
                            Şarjı Durdur
                          </NativeButton>
                        )}
                        {session.is_active && (
                          <NativeButton
                            variant="outline"
                            size="sm"
                            onClick={() => openEndModal(session)}
                            data-testid={`end-session-${session.id}`}
                          >
                            <StopCircle style={{ width: '16px', height: '16px' }} />
                            Bitir
                          </NativeButton>
                        )}
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                  );
                })}
              </NativeTableBody>
            </NativeTable>
          )}
        </NativeCardContent>
      </NativeCard>

      {/* Create Session Modal */}
      <NativeModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Yeni Şarj Oturumu"
        disabled={saving}
      >
        <form onSubmit={handleCreateSubmit}>
          <NativeSelect
            label="İstasyon"
            id="station_id"
            value={createFormData.station_id}
            onChange={(e) => setCreateFormData({...createFormData, station_id: e.target.value})}
            placeholder="İstasyon seçin"
            options={stations.filter(s => s.status === 'available').map((station) => ({ value: station.id, label: station.name }))}
            required
          />

          {isAdmin() && (
            <NativeSelect
              label="Kullanıcı"
              id="user_id"
              value={createFormData.user_id}
              onChange={(e) => setCreateFormData({...createFormData, user_id: e.target.value})}
              placeholder="Kullanıcı seçin"
              options={users.map((u) => ({ value: u.id, label: u.full_name || u.email }))}
              required
            />
          )}

          <NativeSelect
            label="Başlatma Yöntemi"
            id="start_method"
            value={createFormData.start_method}
            onChange={(e) => setCreateFormData({...createFormData, start_method: e.target.value})}
            options={START_METHODS}
          />

          {createFormData.start_method === 'rfid' && (
            <NativeInput
              label="RFID Kart Numarasi"
              id="rfid_card_id"
              value={createFormData.rfid_card_id}
              onChange={(e) => setCreateFormData({...createFormData, rfid_card_id: e.target.value})}
              placeholder="RFID123456"
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
              Iptal
            </NativeButton>
            <NativeButton type="submit" disabled={saving || !createFormData.station_id}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Başlatılıyor...
                </>
              ) : (
                'Oturumu Başlat'
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* End Session Modal */}
      <NativeModal
        isOpen={endModalOpen}
        onClose={() => setEndModalOpen(false)}
        title="Oturumu Sonlandir"
        disabled={saving}
      >
        <form onSubmit={handleEndSubmit}>
          <NativeInput
            label="Tuketilen Enerji (kWh)"
            id="energy_consumed"
            type="number"
            step="0.01"
            min="0"
            value={endFormData.energy_consumed}
            onChange={(e) => setEndFormData({...endFormData, energy_consumed: e.target.value})}
            placeholder="0.00"
          />

          <NativeInput
            label="Tutar (TL)"
            id="cost"
            type="number"
            step="0.01"
            min="0"
            value={endFormData.cost}
            onChange={(e) => setEndFormData({...endFormData, cost: e.target.value})}
            placeholder="0.00"
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setEndModalOpen(false)}>
              Iptal
            </NativeButton>
            <NativeButton type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Sonlandiriliyor...
                </>
              ) : (
                'Oturumu Sonlandir'
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Session Detail Modal */}
      <NativeModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title="Oturum Detayı"
      >
        {detailSession && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Status Badge */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
              <NativeBadge 
                variant={detailSession.is_active ? 'default' : detailSession.status === 'suspended' ? 'warning' : 'secondary'}
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                {detailSession.is_active ? 'Aktif Şarj' : detailSession.status === 'suspended' ? 'Askıda' : 'Tamamlandı'}
              </NativeBadge>
            </div>

            {/* Detail Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '12px',
              backgroundColor: '#f9fafb',
              padding: '16px',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>İşlem Numarası</div>
                <div style={{ fontWeight: '600', fontFamily: 'monospace' }}>{detailSession.transaction_id || detailSession.id?.slice(-8) || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>İstasyon</div>
                <div style={{ fontWeight: '500' }}>{detailSession.station_name || getStationName(detailSession.station_id)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>RFID Kart</div>
                <div style={{ fontFamily: 'monospace', color: '#6366f1' }}>{detailSession.rfid_tag || detailSession.id_tag || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Kullanıcı</div>
                <div style={{ fontWeight: '500' }}>{getUserName(detailSession.user_id)}</div>
              </div>
            </div>

            {/* Time Info */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: '12px',
              backgroundColor: '#f0fdf4',
              padding: '16px',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Başlangıç</div>
                <div style={{ fontWeight: '500', fontSize: '13px' }}>{formatDateTime(detailSession.start_time)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Bitiş</div>
                <div style={{ fontWeight: '500', fontSize: '13px' }}>{detailSession.end_time ? formatDateTime(detailSession.end_time) : '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Süre</div>
                <div style={{ fontWeight: '600', color: '#059669' }}>{formatDuration(detailSession.start_time, detailSession.end_time, detailSession.is_active || detailSession.status === 'active')}</div>
              </div>
            </div>

            {/* Energy & Cost */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '12px',
              backgroundColor: '#eff6ff',
              padding: '16px',
              borderRadius: '8px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Enerji</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>
                  {detailSession.energy_consumed != null ? detailSession.energy_consumed.toFixed(2) : '0.00'}
                  <span style={{ fontSize: '14px', fontWeight: '400', marginLeft: '4px' }}>kWh</span>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Tutar</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                  {detailSession.cost != null ? detailSession.cost.toFixed(2) : '0.00'}
                  <span style={{ fontSize: '14px', fontWeight: '400', marginLeft: '4px' }}>TL</span>
                </div>
              </div>
            </div>

            {/* Stop Reason */}
            {(detailSession.stop_reason || detailSession.reason) && (
              <div style={{ 
                backgroundColor: '#fef2f2',
                padding: '12px 16px',
                borderRadius: '8px',
                borderLeft: '4px solid #ef4444'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Durdurulma Sebebi</div>
                <div style={{ fontWeight: '500', color: '#dc2626' }}>{detailSession.stop_reason || detailSession.reason}</div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <NativeButton variant="outline" onClick={() => setDetailModalOpen(false)}>
                Kapat
              </NativeButton>
              {/* Aktif oturum kontrolü - is_active veya status === 'active' */}
              {(detailSession.is_active || detailSession.status === 'active') && (
                <NativeButton
                  variant="danger"
                  onClick={() => {
                    setDetailModalOpen(false);
                    handleRemoteStop(detailSession);
                  }}
                  data-testid="stop-charging-modal-btn"
                >
                  <Zap style={{ width: '16px', height: '16px' }} />
                  Şarjı Durdur
                </NativeButton>
              )}
            </div>
          </div>
        )}
      </NativeModal>
    </div>
  );
}
